import { useCallback, useMemo, useState, useEffect } from 'react';

import { useMutation, useQuery } from '@zenbusiness/application-commons-graphql';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import AddressLabel from '#client/components/AddressLabel';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import { ADDRESS } from '#client/constants/application';
import { CARD_TYPE } from '#client/constants/cards';
import { MODAL_NAMES } from '#client/constants/modals';
import useAccountDetails from '#client/hooks/useAccountDetails';
import useCardsContext from '#client/hooks/useCardsContext';
import useConst from '#client/hooks/useConst';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { CREATE_CARD_MUTATION } from '#client/mutations';
import { GET_APPLICATION_QUERY } from '#client/queries';

import {
  BoxContentStyled,
  ContentWrapper,
  DialogContentStyled,
  FormControlLabelStyled,
  ImageWrapper,
  SharedAssetStyled
} from '../OpenDebitCardMailing.styles';

const ApprovedGraphicImage = (
  <SharedAssetStyled
    alt=""
    src="/illustration/banking-debit-card-illustration.svg"
  />
);

export default function MailCard() {
  const navigate = useNavigate();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const idempotencyKey = useConst(uuidv4);
  const { unitAccountId } = useUnitAccountId();
  const confirmButtonId = useEnterSubmit();
  const { refetchCard } = useCardsContext();
  const { data } = useFormStep();
  const { createSuccessToast, createErrorToast } = useToastContext();
  const [selectedAddressType, setAddressTypeSelected] = useState(data.addressType);

  const {
    loading: applicationLoading,
    error: applicationError,
    errors,
    data: applicationResponseData
  } = useQuery(GET_APPLICATION_QUERY, {
    noThrow: true,
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    }
  });
  useLogError(errors, applicationError);
  const handleOpenToast = useCallback(
    (title, description, error) => {
      if (error) {
        createErrorToast({ title, description, error });
        return;
      }

      createSuccessToast({ title, description });
      refetchCard();
    },
    [createErrorToast, createSuccessToast, refetchCard]
  );

  useEffect(() => {
    if (applicationError) {
      handleOpenToast(
        'We need your address',
        'To ship your new banking card, we still need your mailing address. Please contact customer support with this info.',
        applicationError
      );
    }
  }, [applicationError, handleOpenToast]);

  const businessAddress = useMemo(
    () => applicationResponseData?.application?.address ?? {},
    [applicationResponseData]
  );

  const { bankCustomerAddress: customerAccountAddress } = useAccountDetails();
  const personalAddress = useMemo(() => customerAccountAddress, [customerAccountAddress]);

  const handleChange = (e, value) => {
    setAddressTypeSelected(value);
  };

  const [createCard, { loading: createCardLoading }] = useMutation(CREATE_CARD_MUTATION, {
    noThrow: true
  });

  const createCardParams = useMemo(
    () => ({
      input: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        attributes: {
          type: CARD_TYPE.BUSINESS_DEBIT_CARD,
          idempotencyKey,
          shippingAddress: selectedAddressType === 'business' ? businessAddress : personalAddress
        }
      }
    }),
    [
      businessEntityUuid,
      unitAccountId,
      selectedAddressType,
      idempotencyKey,
      businessAddress,
      personalAddress
    ]
  );

  const handleSubmitChooseAddress = useCallback(async () => {
    const { error: createCardError } = await createCard({
      variables: createCardParams,
      skip: !unitAccountId
    });

    if (createCardError) {
      // Show toast error
      handleOpenToast('Error', 'Failed to request new card', createCardError);
    } else {
      handleOpenToast('Your new card is on the way', 'It’ll be there in 5 to 7 business days.');
      navigate(`?modal=${MODAL_NAMES.LINK_BANK_ACCOUNT_MODAL}`, { replace: true });
    }
  }, [createCard, createCardParams, handleOpenToast, navigate, unitAccountId]);

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <ImageWrapper>{ApprovedGraphicImage}</ImageWrapper>
            <ContentWrapper>
              <Stack spacing="32px">
                <Heading
                  size="3xl"
                  weight="bold"
                >
                  Where should we send your ZenBusiness Banking card?
                </Heading>
                {applicationLoading || !applicationResponseData ? (
                  <LinearProgress aria-label="progresbar" />
                ) : (
                  <BoxContentStyled>
                    <RadioGroup
                      aria-label="Choose address where we should send the new card."
                      name="address-radio-buttons-group"
                      onChange={handleChange}
                      value={selectedAddressType}
                    >
                      <FormControlLabelStyled
                        control={
                          <Radio
                            inputProps={{
                              'aria-label': ADDRESS.BUSINESS
                            }}
                          />
                        }
                        id="business"
                        label={
                          <AddressLabel
                            {...businessAddress}
                            type="Business Address"
                          />
                        }
                        value={ADDRESS.BUSINESS}
                      />
                      <FormControlLabelStyled
                        control={
                          <Radio
                            inputProps={{
                              'aria-label': ADDRESS.PERSONAL
                            }}
                          />
                        }
                        label={
                          <AddressLabel
                            {...personalAddress}
                            type="Personal Address"
                          />
                        }
                        value={ADDRESS.PERSONAL}
                      />
                    </RadioGroup>
                  </BoxContentStyled>
                )}
                <Text
                  color="primary"
                  size="lg"
                >
                  <Text
                    color="primary"
                    size="lg"
                    weight="medium"
                  >
                    Address not right?
                  </Text>{' '}
                  We&apos;re here to help:{' '}
                  <Link
                    color="info"
                    href="mailto:moneysupport@zenbusiness.com"
                  >
                    moneysupport@zenbusiness.com
                  </Link>{' '}
                  or{' '}
                  <Link
                    color="info"
                    href="tel:1 (844) 4-ZENBIZ"
                  >
                    1 (844) 4-ZENBIZ
                  </Link>
                </Text>
              </Stack>
            </ContentWrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.FULL_WIDTH}>
        <LoadingButton
          color="primary"
          fullWidth
          id={confirmButtonId}
          loading={applicationLoading || createCardLoading}
          onClick={handleSubmitChooseAddress}
          size="large"
          variant="contained"
        >
          Confirm
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}
